// src/components/WordCloud.js
import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import cloud from 'd3-cloud';
// Import removeStopwords AND the default English list 'en'
import { removeStopwords, eng} from 'stopword';

// --- Define Your Custom Stopwords ---
// Add words you want to exclude IN ADDITION to the defaults
const customStopwords = [
    'why', 'when', 'how', 'what', 'who', 'where', 'which', 'whom', 'whose',
    'get', 'got', 'getting', 'give', 'given', 'gives', 'gotten',
    'go', 'goes', 'going', 'went', 'gone',
    'say', 'says', 'said', 'saying',
    'see', 'sees', 'saw', 'seeing',
    'think', 'thinks', 'thought', 'thinking',
    'know', 'knows', 'knew', 'knowing',
    'like', 'likes', 'liked',
    'use', 'uses', 'used', 'using',
    'make', 'makes', 'made', 'making',
    'want', 'wants', 'wanted',
    'come', 'comes', 'came', 'coming',
    'look', 'looks', 'looked', 'looking',
    'take', 'takes', 'took', 'taking',
    'need', 'needs', 'needed',
    'feel', 'feels', 'felt',
    'try', 'tries', 'tried',
    'ask', 'asks', 'asked',
    'work', 'works', 'worked',
    'seem', 'seems', 'seemed',
    'mean', 'means', 'meant',
    'keep', 'keeps', 'kept',
    'let', 'lets',
    'begin', 'begins', 'began',
    'help', 'helps', 'helped',
    'talk', 'talks', 'talked',
    'turn', 'turns', 'turned',
    'start', 'starts', 'started',
    'show', 'shows', 'showed',
    'hear', 'hears', 'heard',
    'play', 'plays', 'played', // Maybe remove if 'played well' is desired?
    'run', 'runs', 'ran',
    'move', 'moves', 'moved',
    'live', 'lives', 'lived',
    'believe', 'believes', 'believed',
    'bring', 'brings', 'brought',
    'happen', 'happens', 'happened',
    'write', 'writes', 'wrote',
    'provide', 'provides', 'provided',
    'sit', 'sits', 'sat',
    'stand', 'stands', 'stood',
    'lose', 'loses', 'lost',
    'pay', 'pays', 'paid',
    'meet', 'meets', 'met',
    'include', 'includes', 'included',
    'continue', 'continues', 'continued',
    'set', 'sets',
    'learn', 'learns', 'learned',
    'change', 'changes', 'changed',
    'lead', 'leads', 'led',
    'understand', 'understands', 'understood',
    'watch', 'watches', 'watched',
    'follow', 'follows', 'followed',
    'stop', 'stops', 'stopped',
    'create', 'creates', 'created',
    'speak', 'speaks', 'spoke',
    'read', 'reads',
    'allow', 'allows', 'allowed',
    'add', 'adds', 'added',
    'spend', 'spends', 'spent',
    'grow', 'grows', 'grew',
    'open', 'opens', 'opened',
    'walk', 'walks', 'walked',
    'win', 'wins', 'won',
    'offer', 'offers', 'offered',
    'remember', 'remembers', 'remembered',
    'love', 'loves', 'loved',
    'consider', 'considers', 'considered',
    'appear', 'appears', 'appeared',
    'buy', 'buys', 'bought',
    'wait', 'waits', 'waited',
    'serve', 'serves', 'served',
    'die', 'dies', 'died',
    'send', 'sends', 'sent',
    'expect', 'expects', 'expected',
    'build', 'builds', 'built',
    'stay', 'stays', 'stayed',
    'fall', 'falls', 'fell',
    'cut', 'cuts',
    'reach', 'reaches', 'reached',
    'kill', 'kills', 'killed',
    'remain', 'remains', 'remained',
    'suggest', 'suggests', 'suggested',
    'raise', 'raises', 'raised',
    'pass', 'passes', 'passed',
    'sell', 'sells', 'sold',
    'require', 'requires', 'required',
    'report', 'reports', 'reported',
    'decide', 'decides', 'decided',
    'pull', 'pulls', 'pulled',
    'back', 'week', 'year', 'game', 'team', // Common nouns you might want to exclude
    'player', 'players', 'guy', 'guys', // Generic person words
    'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', // Numbers as words
    'first', 'second', 'third', 'fourth', 'fifth', // Ordinals
    'lot', 'bit', 'thing', 'things', // Vague nouns
    'it', 'its', // Already likely in default list but safe to add
    'quarterback', 'running', 'back', 'wide', 'receiver', 'tight', 'end', // Football positions (handle multi-word?) - simpler to keep separate
    'qb', 'rb', 'wr', 'te', // Abbreviations
    // Add any other words specific to your domain (fantasy football) that are noise
];
// --- End Custom Stopwords ---


// --- WordCloud Component ---
const WordCloud = ({ snippets, player, onWordSelect }) => {
    const svgRef = useRef(null);

    // Memoize player names to exclude them from the cloud
    const playerNames = useMemo(() => {
        // Handle cases where player or player.name might be null/undefined
        const name = player?.name;
        return typeof name === 'string' ? name.toLowerCase().split(' ') : [];
    }, [player?.name]); // Recompute only if player name changes

    // Memoize the combined stopword list for efficiency
    const combinedStopwords = useMemo(() => {
        // Use Set for efficient merging and deduplication, then convert back to Array
        // Ensure 'eng' and 'customStopwords' are arrays
        const baseStopwords = Array.isArray(eng) ? eng : [];
        const custom = Array.isArray(customStopwords) ? customStopwords : [];
        return Array.from(new Set([...baseStopwords, ...custom]));
    }, []); // Empty dependency array means this runs only once on mount


    // Calculate word frequencies from the provided snippets
    const wordCount = useMemo(() => {
        // Ensure snippets is an array
        const currentSnippets = Array.isArray(snippets) ? snippets : [];
        const counts = {}; // Use a standard object for counts

        currentSnippets.forEach(s => {
            // Ensure snippet text exists and is a string
            const snippetText = (typeof s?.snippet === 'string') ? s.snippet : '';
            if (!snippetText) return; // Skip if no snippet text

            // Split text into potential words using a robust regex (handles punctuation)
            const wordsArray = snippetText.toLowerCase().split(/[\s.,!?;:()"'-]+|\d+/); // Splits by whitespace, common punctuation, and digits

            // Remove stopwords using the combined list
            const filteredWords = removeStopwords(wordsArray, combinedStopwords);

            filteredWords.forEach(word => {
                // Additional checks after stopword removal:
                // - word is truthy (not empty string from splitting)
                // - length > 2 (configurable)
                // - not a player name part
                // - not purely numeric (already handled by split regex, but safe check)
                if (word && word.length > 2 && !playerNames.includes(word) && isNaN(word)) {
                    counts[word] = (counts[word] || 0) + 1;
                }
            });
        });
        return counts;
    }, [snippets, playerNames, combinedStopwords]); // Recompute if these change

    // Generate data structured for d3-cloud, calculating font sizes
    const wordCloudData = useMemo(() => {
        const countsArray = Object.values(wordCount);
        if (countsArray.length === 0) return []; // Return empty if no words counted

        const maxSize = 50; // Max font size
        const minSize = 12; // Min font size
        // Handle case where all counts are the same or only one word exists
        const maxCount = Math.max(...countsArray);
        const minCount = Math.min(...countsArray);
        const range = maxCount - minCount <= 0 ? 1 : maxCount - minCount; // Avoid division by zero

        return Object.entries(wordCount)
            .map(([text, count]) => {
                 // Scale font size logarithmically or linearly between minSize and maxSize
                 // Linear scaling:
                 let size = minSize + ((count - minCount) / range) * (maxSize - minSize);
                 // Ensure size is within bounds and at least minSize
                 size = Math.max(minSize, Math.min(maxSize, size));

                 return {
                    text, // The word itself
                    size: Math.round(size), // Use the calculated size (rounded)
                    value: count // Store original count if needed for tooltips etc.
                };
            })
            .sort((a, b) => b.value - a.value) // Optional: Sort by count descending
            .slice(0, 150); // Limit the number of words displayed for performance/clarity
    }, [wordCount]); // Recompute only when word counts change


    // Callback handler when a word in the cloud is clicked
    const handleWordSelection = useCallback((wordText) => {
        // Ensure wordText is a string before calling the handler from PlayerPage
        if (typeof wordText === 'string' && wordText.length > 0) {
             console.log("WordCloud: Word selected - ", wordText); // Debug log
             onWordSelect?.(wordText); // Call the handler passed from PlayerPage
        } else {
            console.warn("WordCloud: Invalid word text clicked - ", wordText); // Warn if unexpected data
        }
    }, [onWordSelect]); // Dependency is the handler function itself


    // useEffect for D3 drawing logic
    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // Clear previous cloud on re-render

        // Check if we have data to draw
        if (!wordCloudData || wordCloudData.length === 0) {
            // Optional: Display a message if no words are available
             svg.append("text")
                .attr("x", "50%")
                .attr("y", "50%")
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle") // Better vertical centering
                .style("font-size", "14px")
                .style("fill", "currentColor") // Use current text color from CSS
                .attr("class", "text-base-content opacity-60") // Use Tailwind classes
                .text("Not enough data for word cloud.");
            return; // Stop if no data
        }

        // Get container dimensions, default if not available
        const containerWidth = svgRef.current?.parentElement?.clientWidth || 500;
        const containerHeight = 250; // Fixed height, or calculate dynamically

        // Configure the d3-cloud layout generator
        const layout = cloud()
            .size([containerWidth, containerHeight])
            // Map data to ensure 'text' and 'size' properties exist
            .words(wordCloudData.map(d => ({ text: d.text, size: d.size, value: d.value })))
            .padding(5) // Spacing between words
             // Simple rotation logic: prioritize horizontal, some vertical
            .rotate(() => (Math.random() < 0.15 ? 90 : 0)) // ~15% rotated 90 degrees
            .fontSize(d => d.size) // Use the pre-calculated size
            .font('Impact, Arial, sans-serif') // Set font family
            .on('end', draw); // Call the 'draw' function when layout calculation is done

        // Create a color scale
        const colorScale = d3.scaleOrdinal(d3.schemeCategory10);

        layout.start(); // Start the asynchronous layout algorithm

        // Callback function to draw the words once layout is complete
        function draw(words) {
            // Ensure SVG size is set (needed if container size changes)
            svg.attr('width', containerWidth)
               .attr('height', containerHeight);

            // Create a group element centered in the SVG for easier positioning
            const group = svg.append('g')
                .attr('transform', `translate(${containerWidth / 2}, ${containerHeight / 2})`);

            // Bind data and draw text elements using D3's enter/append pattern
            group.selectAll('text')
                .data(words)
                .enter().append('text')
                .style('font-size', d => `${d.size}px`)
                .style('font-family', 'Impact, Arial, sans-serif') // Match layout font
                .style('fill', (d, i) => colorScale(i)) // Color words using the scale
                .attr('text-anchor', 'middle') // Center text horizontally
                .attr('dominant-baseline', 'middle') // Center text vertically (approx)
                .attr('transform', d => `translate(${d.x}, ${d.y}) rotate(${d.rotate})`)
                .text(d => d.text)
                .style('cursor', 'pointer') // Indicate clickable words
                .style('transition', 'opacity 0.2s ease-in-out') // Smooth hover effect
                .on('mouseover', function() { d3.select(this).style('opacity', 0.7); }) // Dim slightly on hover
                .on('mouseout', function() { d3.select(this).style('opacity', 1.0); })
               // Use 'function' for correct 'this' and get datum via d3.select(this).datum()
               .on('click', function(event) { // Use 'function' not arrow '=>'
                const datum = d3.select(this).datum(); // Get datum bound to the clicked element

                // // --- DEBUG LOG 4 (Modified): Inspect the datum retrieved via 'this' ---
                // console.log('DEBUG: Datum retrieved via d3.select(this).datum():', datum);
                // console.log('DEBUG: Value of datum?.text on click:', datum?.text); // Use optional chaining

                // Call the internal handler, passing the text from the retrieved datum
                handleWordSelection(datum?.text); // Use optional chaining here too
                })
                .append("title") // Add a native HTML tooltip showing the count
                .text(d => `${d.text} (Count: ${d.value})`); // Use 'value' which holds the original count
        }
    // Dependencies: Redraw if the data changes or the click handler changes
    }, [wordCloudData, handleWordSelection]); // Removed wordCount dependency, tooltip uses data bound in 'draw'


    // Render the SVG container
    return (
        <div>
            {/* SVG element where the cloud will be drawn */}
            <svg ref={svgRef} className="word-cloud-svg w-full" style={{ minHeight: '250px' }}></svg>
        </div>
    );
};

export default WordCloud;